import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Card, CardContent, Grid, Typography, Box, TextField, Button, Table,
    TableHead, TableBody, TableRow, TableCell, IconButton, Chip,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { collection, query, orderBy, addDoc, deleteDoc, doc } from '@firebase/firestore';
import PageContainer from '../../../../components/container/PageContainer';
import Breadcrumb from '../../../../layouts/full-layout/breadcrumb/Breadcrumb';
import { db } from '../../../../utils/firebase';
import { DEFAULT_DENSITY_G_PER_ML, DEFAULT_GRAMS_PER_PIECE } from '../../utils/units';

// One editable conversion table (densities or piece-weights). Shows the app's
// built-in defaults as read-only baseline rows, with Firestore overrides layered
// on top (an override of the same name replaces the default; deleting it reverts).
function ConversionTable({ title, helper, collectionName, valueField, valueLabel, defaults }) {
    const q = query(collection(db, collectionName), orderBy('name'));
    const [snapshot, loading, error] = useCollection(q);
    const [name, setName] = useState('');
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);

    const handleAdd = async () => {
        const cleanName = name.trim().toLowerCase();
        const num = parseFloat(value);
        if (!cleanName || !Number.isFinite(num) || num <= 0) return;
        setBusy(true);
        try {
            await addDoc(collection(db, collectionName), {
                name: cleanName,
                [valueField]: num,
            });
            setName('');
            setValue('');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (id) => {
        await deleteDoc(doc(db, collectionName, id));
    };

    // Merge built-in defaults with Firestore overrides for display.
    const overrides = {};
    if (snapshot) {
        snapshot.docs.forEach((d) => {
            overrides[d.data().name] = { id: d.id, value: d.data()[valueField] };
        });
    }
    const names = Array.from(
        new Set([...Object.keys(defaults), ...Object.keys(overrides)]),
    ).sort();
    const rows = names.map((n) => {
        const ov = overrides[n];
        const hasDefault = defaults[n] !== undefined;
        return {
            name: n,
            value: ov ? ov.value : defaults[n],
            id: ov ? ov.id : null,
            isOverride: !!ov,
            hasDefault,
        };
    });

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{helper}</Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        label="Ingredient (e.g. flour)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ flex: 1, minWidth: 180 }}
                    />
                    <TextField
                        size="small"
                        label={valueLabel}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        sx={{ width: 160 }}
                    />
                    <Button variant="contained" color="primary" onClick={handleAdd} disabled={busy}>
                        Add
                    </Button>
                </Box>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><Typography variant="h6">Ingredient</Typography></TableCell>
                            <TableCell><Typography variant="h6">{valueLabel}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="h6">Actions</Typography></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={3}>Loading…</TableCell></TableRow>
                        )}
                        {error && (
                            <TableRow><TableCell colSpan={3}>Error loading.</TableCell></TableRow>
                        )}
                        {rows.map((r) => (
                            <TableRow key={r.name}>
                                <TableCell>
                                    {r.name}
                                    {r.isOverride && (
                                        <Chip size="small" color="primary" variant="outlined"
                                            label={r.hasDefault ? 'overridden' : 'custom'}
                                            sx={{ ml: 1 }} />
                                    )}
                                    {!r.isOverride && (
                                        <Chip size="small" variant="outlined" label="default"
                                            sx={{ ml: 1, color: 'text.secondary' }} />
                                    )}
                                </TableCell>
                                <TableCell>{r.value}</TableCell>
                                <TableCell align="right">
                                    {r.isOverride && (
                                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                                            <FeatherIcon icon="trash-2" width={18} />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

ConversionTable.propTypes = {
    title: PropTypes.string.isRequired,
    helper: PropTypes.string.isRequired,
    collectionName: PropTypes.string.isRequired,
    valueField: PropTypes.string.isRequired,
    valueLabel: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    defaults: PropTypes.object.isRequired,
};

export default function Conversions() {
    return (
        <PageContainer title="Admin - Spenza" description="Conversions admin page for Spenza.">
            <Breadcrumb title="Conversions" subtitle="Admin Tools" />
            <Grid container spacing={0}>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        These let the app convert between volume, weight and count for the
                        availability check, the cook-now deduction and add-to-grocery.
                        The app&apos;s built-in defaults are listed below (tagged
                        &quot;default&quot;); add a row with the same ingredient name to
                        override one. Unknown ingredients fall back to the defaults.
                    </Typography>
                    <ConversionTable
                        title="Ingredient densities"
                        helper="Grams per millilitre — lets the app convert volume ↔ weight (e.g. 1 cup flour ↔ grams)."
                        collectionName="ingredientDensities"
                        valueField="gPerMl"
                        valueLabel="g per ml"
                        defaults={DEFAULT_DENSITY_G_PER_ML}
                    />
                    <ConversionTable
                        title="Ingredient piece weights"
                        helper="Grams per single piece — lets the app convert count ↔ weight (e.g. 2 eggs ↔ grams)."
                        collectionName="ingredientPieceWeights"
                        valueField="gramsPerPiece"
                        valueLabel="g per piece"
                        defaults={DEFAULT_GRAMS_PER_PIECE}
                    />
                </Grid>
            </Grid>
        </PageContainer>
    );
}
